import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, ChevronRight } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import { LeaderboardAd, InContentAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';
import NewsletterSignup from '@/components/shared/NewsletterSignup';
import {
  generateArticleSchema,
  generateBreadcrumbSchema,
  generateFAQPageSchema,
  generateOrganizationSchema,
  generateMultipleSchemas,
} from '@/lib/schema';

interface RelatedLink {
  title: string;
  description: string;
  href: string;
}

interface ArticleLayoutProps {
  slug: string;
  title: string;
  description: string;
  category: string;
  datePublished: string;
  dateModified?: string;
  readTime: string;
  faqItems?: Array<{ question: string; answer: string }>;
  relatedLinks?: RelatedLink[];
  children: React.ReactNode;
}

/**
 * Shared layout for long-form editorial articles under /insights/{slug}.
 * Handles SEO meta, Article/Breadcrumb/FAQ structured data, ad placement,
 * and the related-content footer so article files contain only prose.
 */
const ArticleLayout = ({
  slug,
  title,
  description,
  category,
  datePublished,
  dateModified,
  readTime,
  faqItems,
  relatedLinks,
  children,
}: ArticleLayoutProps) => {
  const baseUrl = 'https://wageatlas.com';
  const pageUrl = `${baseUrl}/insights/${slug}/`;

  const schemas: unknown[] = [
    generateOrganizationSchema(baseUrl),
    generateArticleSchema(title, description, pageUrl, baseUrl, datePublished, dateModified || datePublished),
    generateBreadcrumbSchema(
      [
        { name: 'Home', url: `${baseUrl}/` },
        { name: 'Career Insights', url: `${baseUrl}/insights/` },
        { name: title, url: pageUrl },
      ],
      baseUrl
    ),
  ];
  if (faqItems && faqItems.length > 0) {
    schemas.push(generateFAQPageSchema(faqItems));
  }
  const structuredData = generateMultipleSchemas(schemas);

  return (
    <>
      <SeoHead
        title={`${title} - Wage Atlas`}
        description={description}
        canonical={pageUrl}
        type="article"
        schemas={structuredData}
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <article className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/insights" className="hover:text-primary transition-colors">Career Insights</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-foreground truncate">{title}</span>
            </nav>

            {/* Article header */}
            <header className="mb-8 space-y-4">
              <Badge variant="secondary">{category}</Badge>
              <h1 className="text-3xl md:text-4xl font-bold leading-tight">{title}</h1>
              <p className="text-lg text-muted-foreground">{description}</p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(datePublished).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
                <span>{readTime}</span>
                <span>By the Wage Atlas Team</span>
              </div>
            </header>

            <AdFrame className="mb-8">
              <LeaderboardAd />
            </AdFrame>

            {/* Article body */}
            <div className="prose prose-neutral dark:prose-invert max-w-none prose-headings:scroll-mt-24 prose-a:text-primary">
              {children}
            </div>

            <AdFrame className="my-10">
              <InContentAd />
            </AdFrame>

            {/* FAQ section (rendered from the same items used for FAQ schema) */}
            {faqItems && faqItems.length > 0 && (
              <section className="my-10">
                <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {faqItems.map((faq, i) => (
                    <Card key={i}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{faq.question}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground">
                        {faq.answer}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Related content */}
            {relatedLinks && relatedLinks.length > 0 && (
              <section className="my-10">
                <h2 className="text-2xl font-bold mb-6">Keep Exploring</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {relatedLinks.map((link, i) => (
                    <Card key={i} className="hover:shadow-md transition-shadow">
                      <Link to={link.href}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">{link.title}</CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground">
                          {link.description}
                        </CardContent>
                      </Link>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            <NewsletterSignup
              source={`article_${slug.replace(/-/g, '_')}`}
              title="Get Data-Driven Career Insights"
              description="Salary trends, market analysis, and practical career guidance, straight from official labor data."
            />
          </article>
        </div>
      </div>
    </>
  );
};

export default ArticleLayout;
